import React, { useState } from 'react';
import { FileText, ExternalLink, Image as ImageIcon, Download, Share2, Copy, Check } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResultCard = ({ result, token, onShare }) => {
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleDownloadImage = async () => {
    if (!result.image_url) return;
    
    setDownloading(true);
    try {
      const response = await fetch(result.image_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `concurrenz-ai-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Image downloaded successfully');
    } catch (error) {
      toast.error('Failed to download image');
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyAnswer = () => {
    navigator.clipboard.writeText(result.answer);
    setCopied(true);
    toast.success('Answer copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6" data-testid="search-result">
      <Card className="group relative overflow-hidden rounded-xl border bg-card/50 backdrop-blur-md text-card-foreground shadow-lg transition-all hover:shadow-xl hover:border-primary/50 p-4 sm:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="relative space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <h3 className="text-base sm:text-lg font-heading font-semibold text-primary break-words min-w-0 flex-1" data-testid="search-query">{result.query}</h3>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyAnswer}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid="copy-answer-button"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </Button>
              {onShare && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onShare}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid="share-result-button"
                >
                  <Share2 size={16} />
                </Button>
              )}
            </div>
          </div>
          
          {result.image_url && (
            <div className="my-6" data-testid="search-image">
              <div className="relative rounded-xl overflow-hidden border border-border/50 group/image">
                <img 
                  src={result.image_url} 
                  alt={result.query}
                  className="w-full h-auto"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover/image:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-3 right-3 bg-background/80 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-2">
                  <ImageIcon size={14} className="text-primary" />
                  <span className="text-xs font-medium text-foreground">AI Generated</span>
                </div>
                <div className="absolute bottom-3 right-3 opacity-0 group-hover/image:opacity-100 transition-opacity duration-300">
                  <Button
                    size="sm"
                    onClick={handleDownloadImage}
                    disabled={downloading}
                    className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg"
                    data-testid="download-image-button"
                  >
                    <Download size={16} className="mr-2" />
                    {downloading ? 'Downloading...' : 'Download'}
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <div className="prose prose-invert max-w-none">
            <div className="text-base font-answer leading-relaxed text-foreground space-y-4" data-testid="search-answer">
              {result.answer.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="text-foreground/90">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {result.sources && result.sources.length > 0 && (
        <div className="space-y-3" data-testid="search-sources">
          <h4 className="text-sm font-heading font-semibold text-muted-foreground uppercase tracking-wider">Sources</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {result.sources.map((source, idx) => (
              <Card
                key={idx}
                className="p-4 rounded-lg border bg-card/30 backdrop-blur-sm hover:bg-card/50 transition-all cursor-pointer group"
                data-testid={`source-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <FileText className="text-primary mt-1 flex-shrink-0" size={18} />
                  <div className="flex-1 min-w-0">
                    <h5 className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                      {source.title}
                    </h5>
                    <p className="text-xs text-muted-foreground capitalize mt-1">{source.type}</p>
                  </div>
                  <ExternalLink className="text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" size={14} />
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultCard;